import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import clsx from "clsx";
import BottomSheet from "@/components/ui/BottomSheet";

const containerPositionVariant = {
  bottom: "top-full mt-3 left-[50%] translate-x-[-50%]",
  "bottom-left": "top-full mt-3 -right-4",
  "bottom-right": "top-full mt-3 -left-4",
  top: "bottom-full mb-3 left-[50%] translate-x-[-50%]",
  "top-left": "bottom-full mb-3 -right-4",
  "top-right": "bottom-full mb-3 -left-4",
};

const arrowBeforeVariant = {
  bottom: "before:top-[-5px] before:left-[50%] before:translate-x-[-50%]",
  "bottom-left": "before:top-[-5px] before:right-[18px]",
  "bottom-right": "before:top-[-5px] before:left-[18px]",
  top: "before:bottom-[-5px] before:left-[50%] before:translate-x-[-50%]",
  "top-left": "before:bottom-[-5px] before:right-[18px]",
  "top-right": "before:bottom-[-5px] before:left-[18px]",
};

const arrowAfterVariant = {
  bottom: "after:-top-6 after:left-[50%] after:translate-x-[-50%]",
  "bottom-left": "after:-top-6 after:right-[18px]",
  "bottom-right": "after:-top-6 after:left-[18px]",
  top: "after:-bottom-6 after:left-[50%] after:translate-x-[-50%]",
  "top-left": "after:-bottom-6 after:right-[18px]",
  "top-right": "after:-bottom-6 after:left-[18px]",
};

export default function BottomSheetTooltip({
  children,
  content,
  bottomSheetTitle = "",
  position = "bottom",
  contentClassName = "",
  tooltipClassName = "",
  showArrow = true,
}) {
  const isMobile = useIsMobile();
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [isTooltipAnimating, setIsTooltipAnimating] = useState(false);
  const [shouldRenderTooltipDesktop, setShouldRenderTooltipDesktop] =
    useState(false);
  const [shouldRenderTooltipMobile, setShouldRenderTooltipMobile] =
    useState(false);
  const modalRef = useRef(null);
  const leaveTimeoutRef = useRef(null);

  const handleClickOutsideTooltip = useCallback(() => {
    if (isMobile) {
      return;
    }

    if (!isMobile && isTooltipVisible) {
      setIsTooltipVisible(false);
    }
  }, [isMobile, isTooltipVisible]);

  useOnClickOutside(modalRef, handleClickOutsideTooltip);

  const webTooltipClasses = useMemo(() => {
    const isTop = position.startsWith("top");
    const tooltipAnimatingClass = isTop ? "-translate-y-2 scale-95 opacity-0" : "translate-y-2 scale-95 opacity-0"

    return clsx(
      "web-dropdown shadow-xxl absolute z-50 w-[330px] rounded-lg bg-white",
      "origin-top transform p-4 transition-all duration-200 ease-out",
      containerPositionVariant[position],
      showArrow && [
        "before:content-[''] before:w-4 before:h-4 before:bg-white before:absolute before:rotate-45",
        "after:content-[''] after:w-6 after:h-6 after:absolute",
        arrowBeforeVariant[position],
        arrowAfterVariant[position],
      ],
      contentClassName,
      isTooltipAnimating
        ? "translate-y-0 scale-100 opacity-100"
        : tooltipAnimatingClass,
    );
  }, [isTooltipAnimating, position, showArrow]);

  useEffect(() => {
    if (isTooltipVisible && !isMobile) {
      // Web dropdown açılıyor
      setShouldRenderTooltipDesktop(true);
      // Küçük gecikme ile animasyonu tetikle
      const timer = setTimeout(() => {
        setIsTooltipAnimating(true);
      }, 10);
      return () => clearTimeout(timer);
    } else if (!isTooltipVisible && !isMobile) {
      // Web dropdown kapanıyor
      setIsTooltipAnimating(false);
      // Animasyon bitimini bekle, sonra DOM'dan kaldır
      const timer = setTimeout(() => {
        setShouldRenderTooltipDesktop(false);
      }, 200);
      return () => clearTimeout(timer);
    } else if (isTooltipVisible && isMobile) {
      setShouldRenderTooltipMobile(true);
    } else if (!isTooltipVisible && isMobile) {
      setShouldRenderTooltipMobile(false);
    }
  }, [isTooltipVisible, isMobile]);

  useEffect(() => {
    return () => {
      if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);
    };
  }, []);

  return (
    <>
      <div
        className={clsx("relative", tooltipClassName)}
        onClick={() => {
          if (isMobile) {
            setIsTooltipVisible(true);
          }
        }}
        onMouseEnter={() => {
          if (!isMobile) {
            if (leaveTimeoutRef.current) {
              clearTimeout(leaveTimeoutRef.current);
              leaveTimeoutRef.current = null;
            }
            setIsTooltipVisible(true);
          }
        }}
        onMouseLeave={() => {
          if (!isMobile && isTooltipVisible) {
            if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);

            leaveTimeoutRef.current = setTimeout(() => {
              setIsTooltipVisible(false);
              leaveTimeoutRef.current = null;
            }, 100);
          }
        }}
      >
        {children}

        {!isMobile && shouldRenderTooltipDesktop && (
          <div ref={modalRef} className={webTooltipClasses}>
            {content}
          </div>
        )}
      </div>

      <BottomSheet
        title={bottomSheetTitle}
        isOpen={shouldRenderTooltipMobile}
        onClose={() => {
          setIsTooltipVisible(false);
        }}
      >
        <div>{content}</div>
      </BottomSheet>
    </>
  );
}
